import * as React from 'react'
import {DrawSource} from '../../core/enums/DrawSource'
import {DrawType} from '../../core/enums/DrawType'
import {IPopupDispatcher} from '../../core/interfaces/dispatchers'
import {Feature, LineString, Point, VectorLayer, VectorSource, getLength} from '../../core/models'
import {getMapService} from '../../core/reducers/map'
import {MapService} from '../../core/services'
import appConfig from '../../reducers/appConfig'
import {point} from '@turf/helpers'
import turfBearing from '@turf/bearing'
import turfDestination from '@turf/destination'
import {fromLonLat, toLonLat} from 'ol/proj'

interface Props {
  left: string
  popupDispatcher: IPopupDispatcher
  isMesureButtonActive: boolean
  handleIsMesureButtonStateOnClick: () => void
}

interface OlClickEventParams {
  coordinate: [number, number]
  pixel: [number, number]
  originalEvent: MouseEvent
  type: string
}

interface MeasureEditProps {
  feature: Feature
  popupDispatcher: IPopupDispatcher
}

const MeasureEdit: React.FC<MeasureEditProps> = ({feature, popupDispatcher}) => {
  const initial = (() => {
    const v = feature.get('Distance')
    return v ? parseFloat(v).toFixed(2) : getLength(feature.getGeometry()).toFixed(2)
  })()
  const [value, setValue] = React.useState<string>(initial)

  return (
    <div>
      <input
        className="form-control"
        value={value}
        type="number"
        onChange={e => {
          const val = parseFloat(e.target.value).toFixed(2)
          feature.set('Distance', val)

          const coords = feature.getGeometry().getCoordinates() as any
          const startLonLat = toLonLat(coords[0] as any)
          const endLonLat = toLonLat(coords[1] as any)

          const start = point(startLonLat)
          const bearing = turfBearing(start, point(endLonLat))
          const distance = Number(val) / 1000
          const destination = turfDestination(start, distance, bearing, {})
          const newCoordsLonLat = destination.geometry.coordinates
          const newCoords = fromLonLat(newCoordsLonLat)

          feature.getGeometry().setCoordinates([coords[0], newCoords] as any)

          setValue(val)
        }}
      />
      <br />
      <button
        type="button"
        className="button width"
        onClick={() => {
          const layer = getMapService()
            .getLayers()
            .find(l => l.moreInfo.urls[0] == appConfig.reg.measures.url) as VectorLayer
          layer.getSource().removeFeature(feature as any)
          popupDispatcher.removePopups(['measureEdit'])
        }}
      >
        Изтрий
      </button>
      <br />
      <button type="button" className="button width" onClick={() => popupDispatcher.removePopups(['measureEdit'])}>
        ОК
      </button>
    </div>
  )
}

export const MeasureTool: React.FC<Props> = ({
  left,
  popupDispatcher,
  isMesureButtonActive,
  handleIsMesureButtonStateOnClick
}) => {
  const linesSourceRef = React.useRef<VectorSource>(new VectorSource())
  const pointerTraceRef = React.useRef(false)
  const latestFeatRef = React.useRef<Feature | undefined>(undefined)
  const lineRef = React.useRef<LineString | undefined>(undefined)

  const getMap = () => (getMapService() as MapService).map

  const mapOnClick = React.useCallback(
    (event: any) => {
      const e = event as OlClickEventParams
      const result: Feature[] = []
      const map = getMap()
      const pixel = e.pixel
      map.forEachFeatureAtPixel(pixel, (f, layer) => {
        if ((layer as any).moreInfo.urls[0] == appConfig.reg.measures.url) {
          result.push(f as any)
        }
      })
      if (result.length > 0 && result[0].get('Measurable')) {
        popupDispatcher.addPopups({
          measureEdit: {
            content: <MeasureEdit popupDispatcher={popupDispatcher} feature={result[0]} />,
            header: <div>Редакция на измерване</div>,
            width: '400px'
          }
        })
      }
    },
    [popupDispatcher]
  )

  const populateLineSource = React.useCallback((coords: any) => {
    const offset = 0.1
    const ext = [coords[0] - offset, coords[1] - offset, coords[0] + offset, coords[1] + offset]
    const linesSource = linesSourceRef.current

    const parcelLayer = getMapService()
      .getLayers()
      .find(l => l.moreInfo.urls[0] == appConfig.reg.parcels.parcelsUrl && l instanceof VectorLayer) as VectorLayer
    let parcel: Feature | undefined
    if (parcelLayer) {
      try {
        parcelLayer.getSource().forEachFeatureInExtent(ext, (f: any) => {
          parcel = f
          throw new Error()
        })
      } catch {
        /* break out of forEach */
      }
    }
    if (parcel) {
      ;(parcel.getGeometry().getCoordinates() as any).forEach((ring: any) => {
        for (let i = 0; i < ring.length - 1; i++) {
          const lineseg = new Feature()
          lineseg.setGeometry(new LineString([ring[i], ring[i + 1]] as any))
          linesSource.addFeature(lineseg as any)
        }
      })
    }

    const osLayer = getMapService()
      .getLayers()
      .find(l => l.moreInfo.urls[0] == appConfig.reg.regOsLine.id && l instanceof VectorLayer) as VectorLayer
    let regOs: Feature | undefined
    if (osLayer) {
      try {
        osLayer.getSource().forEachFeatureInExtent(ext, (f: any) => {
          regOs = f
          throw new Error()
        })
      } catch {
        /* break out of forEach */
      }
    }
    if (regOs) {
      const oscoords = regOs.getGeometry().getCoordinates() as any
      for (let i = 0; i < oscoords.length - 1; i++) {
        const lineseg = new Feature()
        lineseg.setGeometry(new LineString([oscoords[i], oscoords[i + 1]] as any))
        linesSource.addFeature(lineseg)
      }
    }

    const reglineLayer = getMapService()
      .getLayers()
      .find(l => l.moreInfo.urls[0] == appConfig.reg.regLine.id && l instanceof VectorLayer) as VectorLayer
    let regline: Feature | undefined
    if (reglineLayer) {
      try {
        reglineLayer.getSource().forEachFeatureInExtent(ext, (f: any) => {
          regline = f
          throw new Error()
        })
      } catch {
        /* break out of forEach */
      }
    }
    if (regline) {
      const oscoords = regline.getGeometry().getCoordinates() as any
      for (let i = 0; i < oscoords.length - 1; i++) {
        const lineseg = new Feature()
        lineseg.setGeometry(new LineString([oscoords[i], oscoords[i + 1]] as any))
        linesSource.addFeature(lineseg as any)
      }
    }
  }, [])

  const pointerMoveHandler = React.useCallback(
    (evt: any) => {
      if (evt.dragging) return
      if (!lineRef.current) return

      const coords = evt.coordinate
      const linesSource = linesSourceRef.current

      const perpendicularlayer = getMapService()
        .getLayers()
        .find(l => l.moreInfo.urls[0] == appConfig.reg.perpeniculars.url && l instanceof VectorLayer) as VectorLayer
      if (perpendicularlayer) {
        perpendicularlayer.getSource().clear()
        populateLineSource(coords)
      }

      const closest = linesSource.getClosestFeatureToCoordinate(coords)
      if (closest) {
        const lineCoords = (closest.getGeometry() as LineString).getCoordinates()
        const closestPoint = (closest.getGeometry() as LineString).getClosestPoint(coords)
        const cursorPerpendicular = new LineString([closestPoint, coords] as any)
        if (cursorPerpendicular.getLength() < 300) {
          const startingPoint = lineRef.current.getCoordinates()[0]
          let dx = lineCoords[1][0] - lineCoords[0][0]
          let dy = lineCoords[1][1] - lineCoords[0][1]
          const mag = Math.sqrt(dx * dx + dy * dy)
          dx /= mag
          dy /= mag

          const lambda = dx * (startingPoint[0] - lineCoords[0][0]) + dy * (startingPoint[1] - lineCoords[0][1])
          const x4 = dx * lambda + lineCoords[0][0]
          const y4 = dy * lambda + lineCoords[0][1]
          const feat = new Feature({geometry: new LineString([[x4, y4], startingPoint] as any)})
          feat.setId(x4 + y4 + startingPoint[0])
          perpendicularlayer.getSource().addFeature(feat as any)
        }
      }
      linesSource.clear()

      lineRef.current.setCoordinates([lineRef.current.getCoordinates()[0], coords] as any)
    },
    [populateLineSource]
  )

  const setPointerTrace = React.useCallback(() => {
    if (!pointerTraceRef.current) {
      pointerTraceRef.current = true
      const map = getMap()
      map.on('pointermove', pointerMoveHandler)
      map.on('click', mapOnClick)
    }
  }, [pointerMoveHandler, mapOnClick])

  const activateDraw = React.useCallback(() => {
    setPointerTrace()
    getMapService().drawing.activateDraw(
      DrawType.Point,
      DrawSource.Default,
      () => {
        if (lineRef.current && latestFeatRef.current) {
          const coords = lineRef.current.getCoordinates()
          if (coords[0][0] != coords[1][0]) {
            setTimeout(() => {
              if (latestFeatRef.current!.get('Finished')) return
              const layer = getMapService()
                .getLayers()
                .find(l => l.moreInfo.urls[0] == appConfig.reg.measures.url) as VectorLayer
              layer.getSource().removeFeature(latestFeatRef.current! as any)
              lineRef.current = undefined
              linesSourceRef.current.clear()
              const perpendicularlayer = getMapService()
                .getLayers()
                .find(
                  l => l.moreInfo.urls[0] == appConfig.reg.perpeniculars.url && l instanceof VectorLayer
                ) as VectorLayer
              if (perpendicularlayer) {
                perpendicularlayer.getSource().clear()
              }
            }, 300)
          }
        }
      },
      f => {
        const perpendicularlayer = getMapService()
          .getLayers()
          .find(l => l.moreInfo.urls[0] == appConfig.reg.perpeniculars.url && l instanceof VectorLayer) as VectorLayer
        if (perpendicularlayer) {
          perpendicularlayer.getSource().clear()
        }
        if (lineRef.current) {
          const first = lineRef.current.getCoordinates()[0]
          const coord = (f.getGeometry() as Point).getCoordinates()
          lineRef.current.setCoordinates([first, coord] as any)
          if (latestFeatRef.current) {
            latestFeatRef.current.set('Finished', true)
          }
          lineRef.current = undefined
        } else {
          const coord = (f.getGeometry() as Point).getCoordinates()
          const line = new LineString([coord, coord] as any)
          const layer = getMapService()
            .getLayers()
            .find(l => l.moreInfo.urls.indexOf(appConfig.reg.measures.url) != -1)! as VectorLayer
          const feat = new Feature()
          feat.set('Measurable', true)
          feat.setGeometry(line)
          layer.getSource().addFeature(feat as any)
          latestFeatRef.current = feat
          lineRef.current = line
          activateDraw()
        }
      }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setPointerTrace])

  React.useEffect(() => {
    const mapService = getMapService()
    if (isMesureButtonActive) {
      activateDraw()
    } else if (mapService?.drawing) {
      mapService.drawing.deactivateDraw(true)
    }
  }, [isMesureButtonActive, activateDraw])

  return (
    <div className="measure-tool" style={{left}} title="Измерване" onClick={handleIsMesureButtonStateOnClick}>
      <img
        alt=""
        src="resources/icons/ruler1.png"
        style={{width: '28px', height: '28px', backgroundColor: isMesureButtonActive ? '#26751b' : '#5c83b3b5'}}
      />
    </div>
  )
}
